import React, { useEffect, useRef, useState, useCallback } from 'react'
import {
  Loader2,
  FileText,
  ChevronUp,
  ChevronDown,
  ZoomIn,
  ZoomOut,
  Sparkles,
  MessageSquare,
  Send,
  X
} from 'lucide-react'

interface SelectionPopup {
  x: number
  y: number
  text: string
}

interface LabGuidePdfViewerProps {
  pdfUrl: string | null
  pageStart: number | null
  pageEnd: number | null
  moduleTitle: string | null
  exerciseTitle: string | null
  refCode?: string | null
  isLoading: boolean
  error: string | null
  onSendToChat: (displayMessage: string, fullMessage: string) => void
  renderExternalToolbar?: (toolbar: React.ReactNode) => void
}

export const LabGuidePdfViewer: React.FC<LabGuidePdfViewerProps> = ({
  pdfUrl,
  pageStart,
  pageEnd,
  moduleTitle,
  exerciseTitle,
  refCode,
  isLoading,
  error,
  onSendToChat,
  renderExternalToolbar
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [pdfDoc, setPdfDoc] = useState<any>(null)
  const [totalPdfPages, setTotalPdfPages] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [scaleAdjust, setScaleAdjust] = useState(0)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [pdfError, setPdfError] = useState<string | null>(null)
  const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map())
  const textLayerRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const renderTasksRef = useRef<Map<number, any>>(new Map())
  const renderingRef = useRef<Set<number>>(new Set())
  const observerRef = useRef<IntersectionObserver | null>(null)

  const [popup, setPopup] = useState<SelectionPopup | null>(null)
  const [askMode, setAskMode] = useState(false)
  const [askInput, setAskInput] = useState('')
  const popupRef = useRef<HTMLDivElement>(null)
  const askInputRef = useRef<HTMLInputElement>(null)
  const isTransitioningToAskRef = useRef(false)
  const [containerWidth, setContainerWidth] = useState(0)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  const firstPage = pageStart ?? 1
  const lastPage = pageEnd ?? totalPdfPages
  const pageCount = Math.max(0, lastPage - firstPage + 1)

  const BASE_PDF_WIDTH = 612
  const autoScale = containerWidth > 0 ? containerWidth / BASE_PDF_WIDTH : 1.2
  const scale = Math.min(2.5, Math.max(0.4, autoScale + scaleAdjust))

  useEffect(() => {
    if (!pdfUrl) return
    let cancelled = false

    setPdfLoading(true)
    setPdfError(null)
    setPdfDoc(null)
    setTotalPdfPages(0)
    setCurrentPage(firstPage)
    setPopup(null)
    canvasRefs.current.clear()
    textLayerRefs.current.clear()
    renderTasksRef.current.forEach((t) => t?.cancel?.())
    renderTasksRef.current.clear()
    renderingRef.current.clear()

    const loadPdf = async () => {
      try {
        const pdfjsLib = await import('pdfjs-dist')
        if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
          pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
            'pdfjs-dist/build/pdf.worker.min.mjs',
            import.meta.url
          ).toString()
        }
        const doc = await pdfjsLib.getDocument({ url: pdfUrl, withCredentials: false }).promise
        if (cancelled) return
        setPdfDoc(doc)
        setTotalPdfPages(doc.numPages)
        setPdfLoading(false)
      } catch (err: any) {
        if (!cancelled) {
          setPdfError('Failed to load PDF.')
          setPdfLoading(false)
        }
      }
    }

    loadPdf()
    return () => {
      cancelled = true
    }
  }, [pdfUrl])

  useEffect(() => {
    setCurrentPage(firstPage)
  }, [firstPage])

  const scaleRef = useRef(scale)
  useEffect(() => {
    scaleRef.current = scale
  }, [scale])

  const renderPage = useCallback(
    async (pageNum: number) => {
      if (!pdfDoc) return
      const canvas = canvasRefs.current.get(pageNum)
      const textLayerDiv = textLayerRefs.current.get(pageNum)
      if (!canvas) return

      const existing = renderTasksRef.current.get(pageNum)
      if (existing) {
        try {
          existing.cancel()
        } catch {}
        renderTasksRef.current.delete(pageNum)
      }

      if (renderingRef.current.has(pageNum)) return
      renderingRef.current.add(pageNum)

      try {
        const pdfjsLib = await import('pdfjs-dist')
        const page = await pdfDoc.getPage(pageNum)

        const currentScale = scaleRef.current
        const dpr = window.devicePixelRatio || 1
        const viewport = page.getViewport({ scale: currentScale })
        const renderViewport = page.getViewport({ scale: currentScale * dpr })

        canvas.width = renderViewport.width
        canvas.height = renderViewport.height
        canvas.style.width = `${viewport.width}px`
        canvas.style.height = `${viewport.height}px`

        const ctx = canvas.getContext('2d')
        if (!ctx) return

        ctx.clearRect(0, 0, canvas.width, canvas.height)
        const renderTask = page.render({ canvasContext: ctx, viewport: renderViewport })
        renderTasksRef.current.set(pageNum, renderTask)
        await renderTask.promise
        renderTasksRef.current.delete(pageNum)

        if (textLayerDiv) {
          textLayerDiv.innerHTML = ''
          textLayerDiv.style.width = `${viewport.width}px`
          textLayerDiv.style.height = `${viewport.height}px`

          const textContent = await page.getTextContent()
          if (textContent.items.length > 0) {
            const textLayer = new pdfjsLib.TextLayer({
              textContentSource: page.streamTextContent(),
              container: textLayerDiv,
              viewport
            })
            await textLayer.render()
          }
        }
      } catch (err: any) {
        if (err?.name !== 'RenderingCancelledException') {
          console.error(`Error rendering page ${pageNum}:`, err)
        }
      } finally {
        renderingRef.current.delete(pageNum)
      }
    },
    [pdfDoc]
  )

  useEffect(() => {
    if (!pdfDoc) return
    canvasRefs.current.forEach((_, pageNum) => renderPage(pageNum))
  }, [scale, pdfDoc, renderPage])

  const renderAllMounted = useCallback(() => {
    canvasRefs.current.forEach((_, pageNum) => renderPage(pageNum))
  }, [renderPage])

  useEffect(() => {
    if (!containerRef.current) return
    let debounceTimer: ReturnType<typeof setTimeout> | null = null

    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width
        if (w === 0) continue
        const newAutoScale = w / BASE_PDF_WIDTH
        scaleRef.current = Math.min(2.5, Math.max(0.4, newAutoScale + scaleAdjust))

        if (debounceTimer) clearTimeout(debounceTimer)
        debounceTimer = setTimeout(() => {
          setContainerWidth(w)
        }, 150)
      }
    })
    resizeObserverRef.current.observe(containerRef.current)
    setContainerWidth(containerRef.current.clientWidth)
    return () => {
      resizeObserverRef.current?.disconnect()
      if (debounceTimer) clearTimeout(debounceTimer)
    }
  }, [renderAllMounted, scaleAdjust])

  useEffect(() => {
    if (!pdfDoc || totalPdfPages === 0) return
    observerRef.current?.disconnect()
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const pageNum = parseInt((entry.target as HTMLElement).dataset.page || '0')
            if (pageNum > 0) renderPage(pageNum)
          }
        })
      },
      { root: containerRef.current, rootMargin: '200px', threshold: 0 }
    )
    containerRef.current
      ?.querySelectorAll('[data-page]')
      .forEach((el) => observerRef.current?.observe(el))
    return () => observerRef.current?.disconnect()
  }, [pdfDoc, totalPdfPages, firstPage, lastPage, renderPage])

  const handleScroll = () => {
    if (!containerRef.current) return
    const container = containerRef.current
    let nearest = firstPage,
      minDist = Infinity
    container.querySelectorAll('[data-page]').forEach((el) => {
      const dist = Math.abs(el.getBoundingClientRect().top - container.getBoundingClientRect().top)
      const p = parseInt((el as HTMLElement).dataset.page || '0')
      if (dist < minDist) {
        minDist = dist
        nearest = p
      }
    })
    setCurrentPage(nearest)
  }

  const scrollToPage = useCallback((p: number) => {
    containerRef.current
      ?.querySelector(`[data-page="${p}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const handleMouseUp = useCallback(() => {
    setTimeout(() => {
      if (isTransitioningToAskRef.current) return
      const selection = window.getSelection()
      const text = selection?.toString().trim()
      if (!text || text.length < 3) {
        setPopup(null)
        return
      }

      const range = selection!.getRangeAt(0)
      const rect = range.getBoundingClientRect()
      const container = containerRef.current!
      const containerRect = container.getBoundingClientRect()

      const x = Math.min(rect.left + rect.width / 2 - containerRect.left, containerRect.width - 220)
      const y = rect.top - containerRect.top + container.scrollTop - 8

      setPopup({ x: Math.max(8, x), y, text })
      setAskMode(false)
      setAskInput('')
    }, 10)
  }, [])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (isTransitioningToAskRef.current) return
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) setPopup(null)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    if (askMode) setTimeout(() => askInputRef.current?.focus(), 50)
  }, [askMode])

  const handleExplain = () => {
    if (!popup) return
    const truncated = popup.text.length > 500 ? popup.text.slice(0, 500) + '…' : popup.text
    const contextLine = [moduleTitle, exerciseTitle].filter(Boolean).join(' → ')
    const context = contextLine ? `Context: ${contextLine}\n` : ''
    const displayMessage = `Please explain this from the lab guide:\n\n"${truncated}"`
    onSendToChat(displayMessage, `${context}${displayMessage}`)
    setPopup(null)
    window.getSelection()?.removeAllRanges()
  }

  const handleAskSubmit = () => {
    if (!popup || !askInput.trim()) return
    const truncated = popup.text.length > 500 ? popup.text.slice(0, 500) + '…' : popup.text
    const contextLine = [moduleTitle, exerciseTitle].filter(Boolean).join(' → ')
    const context = contextLine ? `Context: ${contextLine}\n` : ''
    const displayMessage = `Regarding this from the lab guide:\n\n"${truncated}"\n\nMy question: ${askInput.trim()}`
    onSendToChat(displayMessage, `${context}${displayMessage}`)
    setPopup(null)
    setAskInput('')
    window.getSelection()?.removeAllRanges()
  }

  const zoomIn = useCallback(() => setScaleAdjust((s) => Math.min(s + 0.15, 1.3)), [])
  const zoomOut = useCallback(() => setScaleAdjust((s) => Math.max(s - 0.15, -0.6)), [])

  useEffect(() => {
    if (!renderExternalToolbar) return
    renderExternalToolbar(
      <div className="flex items-center gap-2">
      
        <div className="flex items-center gap-1 text-xs text-gray-600 bg-white border border-gray-200 rounded-md px-2 py-1">
          <button
            onClick={() => scrollToPage(Math.max(firstPage, currentPage - 1))}
            disabled={currentPage <= firstPage}
            className="hover:text-black disabled:opacity-30 transition-colors"
          >
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
          <span className="px-1 tabular-nums">
            {currentPage - firstPage + 1} / {pageCount}
          </span>
          <button
            onClick={() => scrollToPage(Math.min(lastPage, currentPage + 1))}
            disabled={currentPage >= lastPage}
            className="hover:text-black disabled:opacity-30 transition-colors"
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-md px-1 py-1">
          <button
            onClick={zoomOut}
            disabled={scaleAdjust <= -0.6}
            className="p-0.5 hover:text-black disabled:opacity-30 transition-colors text-gray-600"
          >
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <span className="text-xs text-gray-600 w-10 text-center tabular-nums">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            disabled={scaleAdjust >= 1.3}
            className="p-0.5 hover:text-black disabled:opacity-30 transition-colors text-gray-600"
          >
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
        </div>
          {/* {refCode && (
          <div className="flex items-center gap-1.5">
            <FileText className="w-4 h-4" style={{ color: '#7a6020' }} />
            <span className="text-xs font-semibold" style={{ color: '#7a6020' }}>{refCode}</span>
          </div>
        )} */}
      </div>
    )
  }, [
    renderExternalToolbar,
    refCode,
    currentPage,
    pageCount,
    firstPage,
    lastPage,
    scale,
    scaleAdjust,
    zoomIn,
    zoomOut,
    scrollToPage
  ])

  if (isLoading) return <CenteredState icon="loader" message="Loading lab guide..." />
  if (error || pdfError)
    return (
      <CenteredState icon="file" message="Unable to load lab guide" sub={error || pdfError || ''} />
    )
  if (!pdfUrl)
    return (
      <CenteredState
        icon="file"
        message="No lab guide available"
        sub="This module doesn't have an associated lab guide document."
      />
    )
  if (pdfLoading) return <CenteredState icon="loader" message="Rendering PDF..." />

  const pagesToRender = Array.from(
    { length: Math.max(0, lastPage - firstPage + 1) },
    (_, i) => firstPage + i
  ).filter((p) => p >= 1 && p <= totalPdfPages)
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto relative select-text pdf-scrollbar"
        onScroll={handleScroll}
        onMouseUp={handleMouseUp}
      >
        {pagesToRender.map((pageNum) => (
          <div key={pageNum} data-page={pageNum}>
            <div className="relative bg-white overflow-hidden w-full">
              <canvas
                ref={(el) => {
                  if (el) canvasRefs.current.set(pageNum, el)
                  else canvasRefs.current.delete(pageNum)
                }}
                className="block"
              />
              <div
                ref={(el) => {
                  if (el) textLayerRefs.current.set(pageNum, el)
                  else textLayerRefs.current.delete(pageNum)
                }}
                className="absolute inset-0 textLayer"
                style={{
                  pointerEvents: 'auto',
                  zIndex: 2,
                  userSelect: 'text',
                  WebkitUserSelect: 'text'
                }}
              />
            </div>
          </div>
        ))}

        {popup && (
          <div
            ref={popupRef}
            className="absolute z-50"
            style={{ left: popup.x, top: popup.y, transform: 'translate(-50%, -100%)' }}
          >
            <div className="relative">
              <div
                className="bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
                style={{ minWidth: 200 }}
              >
                {!askMode ? (
                  <div className="flex items-stretch divide-x divide-gray-100">
                    <button
                      onClick={handleExplain}
                      className="flex items-center gap-2 px-4 py-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors flex-1"
                    >
                      <Sparkles className="w-3.5 h-3.5 text-black shrink-0" />
                      Explain
                    </button>
                    <button
                      onClick={() => {
                        isTransitioningToAskRef.current = true
                        setAskMode(true)
                        setTimeout(() => {
                          isTransitioningToAskRef.current = false
                        }, 200)
                      }}
                      className="flex items-center gap-2 px-4 py-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors flex-1"
                    >
                      <MessageSquare className="w-3.5 h-3.5 text-black shrink-0" />
                      Ask
                    </button>
                    <button
                      onClick={() => {
                        setPopup(null)
                        window.getSelection()?.removeAllRanges()
                      }}
                      className="flex items-center px-2.5 py-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="p-2">
                    <div className="text-xs text-gray-400 px-1 pb-1.5 truncate max-w-xs">
                      "{popup.text.slice(0, 60)}
                      {popup.text.length > 60 ? '…' : ''}"
                    </div>
                    <div className="flex items-center gap-1.5">
                      <input
                        ref={askInputRef}
                        type="text"
                        value={askInput}
                        onChange={(e) => setAskInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleAskSubmit()
                          if (e.key === 'Escape') setAskMode(false)
                        }}
                        placeholder="Ask a question..."
                        className="flex-1 text-xs px-2.5 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-black text-gray-800 placeholder:text-gray-400"
                      />
                      <button
                        onClick={handleAskSubmit}
                        disabled={!askInput.trim()}
                        className="p-1.5 rounded-lg bg-black text-white hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shrink-0"
                      >
                        <Send className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => setAskMode(false)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-center">
                <div className="w-2.5 h-2.5 bg-white border-r border-b border-gray-200 rotate-45 -mt-1.5 shadow-sm" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const CenteredState = ({
  icon,
  message,
  sub
}: {
  icon: 'loader' | 'file'
  message: string
  sub?: string
}) => (
  <div className="flex flex-col items-center justify-center h-full gap-3 bg-[#f0e8d8] px-6 text-center">
    {icon === 'loader' ? (
      <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
    ) : (
      <FileText className="w-10 h-10 text-gray-300" />
    )}
    <p className="text-sm font-medium text-gray-600">{message}</p>
    {sub && <p className="text-xs text-gray-400">{sub}</p>}
  </div>
)
